import { useNavigate, useLocation } from "react-router-dom";
import { useState, createContext, useContext } from 'react';
import { Tooltip } from '@mui/material';
import Iconify from "src/components/Iconify";
import { verifyRules } from "src/utils/functions";
import { Sheet, SheetContent, SheetTrigger } from "src/@/components/ui/sheet";

const SidebarContext = createContext({ isExpanded: false });

const SidebarMenuButton = ({ title, onClick, path, icon }) => {
    const { isExpanded } = useContext(SidebarContext);
    const location = useLocation();

    // Check strict or nested path
    const active = location.pathname === path || location.pathname.startsWith(path + '/');

    const ButtonContent = (
        <button
            onClick={onClick}
            className={`
                group relative flex w-full items-center gap-3 rounded-xl px-3 py-1.5 text-[13px]
                transition-all duration-200 ease-in-out border border-transparent
                ${!isExpanded ? 'justify-center px-0' : ''}
                ${active
                    ? 'bg-[#7C3AED]/10 text-[#7C3AED]'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }
            `}
        >
            {icon && (
                <Iconify
                    icon={icon}
                    width={16}
                    height={16}
                    className={`
                        transition-colors duration-200 flex-shrink-0
                        ${active ? 'text-[#7C3AED]' : 'text-gray-400 group-hover:text-gray-700'}
                    `}
                />
            )}

            {isExpanded && (
                <>
                    <span className="truncate flex-1 text-left animate-in fade-in duration-200">
                        {title}
                    </span>
                    {active && (
                        <div className="w-1.5 h-1.5 rounded-full bg-[#7C3AED]" />
                    )}
                </>
            )}
        </button>
    );

    if (!isExpanded) {
        return (
            <Tooltip title={title} placement="right" arrow>
                {ButtonContent}
            </Tooltip>
        );
    }

    return ButtonContent;
};

const SidebarGroup = ({ children, label }) => {
    const { isExpanded } = useContext(SidebarContext);
    const [isOpen, setIsOpen] = useState(true);

    const toggle = () => setIsOpen(!isOpen);

    return (
        <div className="mb-4 last:mb-0">
            {isExpanded && label && (
                <div
                    onClick={toggle}
                    className="flex items-center justify-between px-3 mb-2 animate-in fade-in duration-200 group cursor-pointer hover:bg-gray-50/50 rounded-md py-1 select-none"
                >
                    <span className="text-sm font-semibold text-gray-900">
                        {label}
                    </span>
                    <Iconify
                        icon="solar:alt-arrow-down-linear"
                        width={16}
                        height={16}
                        className={`text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                    />
                </div>
            )}
            <div className={`space-y-0.5 overflow-hidden transition-all duration-300 ease-in-out ${!isExpanded || isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                {children}
            </div>
        </div>
    );
};

export default function SidebarDashboard({ user }) {
    const navigate = useNavigate();
    const isAdmin = user?.level === "root";
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    const isExpanded = !isCollapsed || isHovered;

    const renderNavContent = (onNavigate) => (
        <nav>
            <SidebarGroup label="Conta">
                <SidebarMenuButton
                    title="Perfil"
                    path="/dashboard/conta/perfil"
                    icon="solar:user-circle-bold"
                    onClick={() => { navigate('/dashboard/conta/perfil'); onNavigate?.(); }}
                />
                {verifyRules(user.rules, "conta.consumo.ver") && (
                    <SidebarMenuButton
                        title="Consumo"
                        path="/dashboard/conta/consumo"
                        icon="solar:chart-2-bold"
                        onClick={() => { navigate('/dashboard/conta/consumo'); onNavigate?.(); }}
                    />
                )}
            </SidebarGroup>

            <SidebarGroup label="Chats">
                {(verifyRules(user.rules, "respostas_rapidas") || isAdmin) && (
                    <SidebarMenuButton
                        title="Respostas Rápidas"
                        path="/dashboard/configuracoes/respostas-rapidas"
                        icon="solar:chat-square-like-bold"
                        onClick={() => { navigate('/dashboard/configuracoes/respostas-rapidas'); onNavigate?.(); }}
                    />
                )}
                <SidebarMenuButton
                    title="Etiquetas"
                    path="/dashboard/configuracoes/tags"
                    icon="solar:tag-bold"
                    onClick={() => { navigate('/dashboard/configuracoes/tags'); onNavigate?.(); }}
                />
                {isAdmin && (
                    <SidebarMenuButton
                        title="Direcionamentos"
                        path="/dashboard/configuracoes/direcionamentos"
                        icon="solar:routing-2-bold"
                        onClick={() => { navigate('/dashboard/configuracoes/direcionamentos'); onNavigate?.(); }}
                    />
                )}
            </SidebarGroup>

            <SidebarGroup label="Configurações">
                {(verifyRules(user.rules, "canais") || isAdmin) && (
                    <SidebarMenuButton
                        title="Canais"
                        path="/dashboard/configuracoes/canais"
                        icon="fluent:channel-share-28-filled"
                        onClick={() => { navigate('/dashboard/configuracoes/canais'); onNavigate?.(); }}
                    />
                )}
                {(verifyRules(user.rules, "gerenciar_usuarios") || isAdmin) && (
                    <SidebarMenuButton
                        title="Usuários"
                        path="/dashboard/configuracoes/usuarios"
                        icon="solar:users-group-rounded-bold"
                        onClick={() => { navigate('/dashboard/configuracoes/usuarios'); onNavigate?.(); }}
                    />
                )}
                {(verifyRules(user.rules, "gerenciar_setores") || isAdmin) && (
                    <SidebarMenuButton
                        title="Setores"
                        path="/dashboard/configuracoes/setores"
                        icon="mingcute:department-line"
                        onClick={() => { navigate('/dashboard/configuracoes/setores'); onNavigate?.(); }}
                    />
                )}
                {(verifyRules(user.rules, "gerenciar_funis") || isAdmin) && (
                    <SidebarMenuButton
                        title="Funil"
                        path="/dashboard/configuracoes/funil-vendas"
                        icon="mdi:funnel-outline"
                        onClick={() => { navigate('/dashboard/configuracoes/funil-vendas'); onNavigate?.(); }}
                    />
                )}
                {(verifyRules(user.rules, "gerenciar_integrações") || isAdmin) && (
                    <SidebarMenuButton
                        title="Integrações"
                        path="/dashboard/configuracoes/integracoes"
                        icon="solar:plug-circle-bold"
                        onClick={() => { navigate('/dashboard/configuracoes/integracoes'); onNavigate?.(); }}
                    />
                )}
                <SidebarMenuButton
                    title="APIs & Webhooks"
                    path="/dashboard/configuracoes/apis"
                    icon="solar:key-bold"
                    onClick={() => { navigate('/dashboard/configuracoes/apis'); onNavigate?.(); }}
                />
                {verifyRules(user.rules, "gerenciar_calendario") && (
                    <SidebarMenuButton
                        title="Calendário"
                        path="/dashboard/configuracoes/calendarios"
                        icon="solar:calendar-bold"
                        onClick={() => { navigate('/dashboard/configuracoes/calendarios'); onNavigate?.(); }}
                    />
                )}
                {(isAdmin || verifyRules(user.rules, "chatbots.gerenciar")) && (
                    <SidebarMenuButton
                        title="Chatflows"
                        path="/dashboard/configuracoes/chatbots"
                        icon="solar:chat-square-code-bold"
                        onClick={() => { navigate('/dashboard/configuracoes/chatbots'); onNavigate?.(); }}
                    />
                )}
                <SidebarMenuButton
                    title="Chamados"
                    path="/dashboard/configuracoes/chamados-config"
                    icon="eva:clipboard-outline"
                    onClick={() => { navigate('/dashboard/configuracoes/chamados-config'); onNavigate?.(); }}
                />
                <SidebarMenuButton
                    title="Tarefas"
                    path="/dashboard/configuracoes/tarefas-config"
                    icon="mdi:clipboard-check-outline"
                    onClick={() => { navigate('/dashboard/configuracoes/tarefas-config'); onNavigate?.(); }}
                />
            </SidebarGroup>

            {(isAdmin || verifyRules(user.rules, "agentes_ia")) && (
                <SidebarGroup label="Inteligência Artificial">
                    {isAdmin && (
                        <SidebarMenuButton
                            title="Copilot"
                            path="/dashboard/configuracoes/copilot"
                            icon="solar:magic-stick-3-bold"
                            onClick={() => { navigate('/dashboard/configuracoes/copilot'); onNavigate?.(); }}
                        />
                    )}
                    {verifyRules(user.rules, "agentes_ia") && (
                        <SidebarMenuButton
                            title="Agentes"
                            path="/dashboard/configuracoes/agentes-ia"
                            icon="mingcute:head-ai-fill"
                            onClick={() => { navigate('/dashboard/configuracoes/agentes-ia'); onNavigate?.(); }}
                        />
                    )}
                </SidebarGroup>
            )}

            {(isAdmin || verifyRules(user.rules, "cadastro.gerenciar")) && (
                <SidebarGroup label="Cadastro">
                    <SidebarMenuButton
                        title="Serviços"
                        path="/dashboard/configuracoes/servicos"
                        icon="material-symbols-light:service-toolbox-rounded"
                        onClick={() => { navigate('/dashboard/configuracoes/servicos'); onNavigate?.(); }}
                    />
                    <SidebarMenuButton
                        title="Produtos"
                        path="/dashboard/configuracoes/produtos"
                        icon="solar:case-bold"
                        onClick={() => { navigate('/dashboard/configuracoes/produtos'); onNavigate?.(); }}
                    />
                </SidebarGroup>
            )}
        </nav>
    );

    return (
        <>
            {/* Mobile: hamburger + Sheet drawer */}
            <div className="md:hidden sticky top-0 z-40 bg-white border-b border-gray-100 px-4 py-2">
                <SidebarContext.Provider value={{ isExpanded: true }}>
                    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                        <SheetTrigger asChild>
                            <button className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                                <Iconify icon="solar:hamburger-menu-linear" width={20} />
                                <span>Menu</span>
                            </button>
                        </SheetTrigger>
                        <SheetContent side="left" className="w-[280px] p-0 pt-8">
                            <div className="flex flex-col flex-1 overflow-y-auto py-2 px-4">
                                {renderNavContent(() => setMobileOpen(false))}
                            </div>
                        </SheetContent>
                    </Sheet>
                </SidebarContext.Provider>
            </div>

            {/* Desktop: original aside sidebar */}
            <SidebarContext.Provider value={{ isExpanded }}>
                <aside
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    className={`
                    hidden md:flex flex-col flex-shrink-0 border-r border-gray-100 bg-white h-full relative z-20
                    transition-all duration-300 ease-in-out
                    ${isExpanded ? 'w-[260px]' : 'w-[80px]'}
                `}
                >
                    {/* Collapse Button - Absolute on border */}
                    <button
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        className={`
                        absolute top-8 -right-3 z-50
                        flex items-center justify-center w-6 h-6 rounded-full
                        bg-white border border-gray-200 shadow-sm
                        text-gray-500 hover:text-gray-900 hover:bg-gray-50
                        transition-all duration-200
                        cursor-pointer
                    `}
                        title={isCollapsed ? "Fixar Menu" : "Recolher Menu"}
                    >
                        <Iconify
                            icon={isCollapsed ? "solar:pin-bold" : "solar:alt-arrow-left-linear"}
                            width={14}
                            height={14}
                            className={`transition-transform duration-200 ${isCollapsed ? 'rotate-45' : ''}`}
                        />
                    </button>

                    <div className={`flex flex-col flex-1 overflow-y-auto py-4 scrollbar-thin scrollbar-thumb-gray-100 hover:scrollbar-thumb-gray-200 ${isExpanded ? 'px-4' : 'px-2'}`}>
                        {renderNavContent()}
                    </div>
                </aside>
            </SidebarContext.Provider>
        </>
    );
}
